import { Analytics } from '@vercel/analytics/react'
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { UserButton, useUser, SignInButton } from '@clerk/clerk-react'

// ─── FLOATING PARTICLES ───────────────────────────────────────────────────────
function FloatingParticles() {
  const particles = useRef(
    Array.from({ length: 22 }, (_, i) => ({
      id: i, left: Math.random() * 100, size: Math.random() * 3 + 1,
      delay: Math.random() * 6, duration: Math.random() * 10 + 8, opacity: Math.random() * 0.18 + 0.04,
    }))
  ).current
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', bottom: -10, left: p.left + '%',
          width: p.size, height: p.size, borderRadius: '50%',
          background: '#4a9eff', opacity: p.opacity,
          animation: `particleRise ${p.duration}s ${p.delay}s linear infinite`,
        }} />
      ))}
    </div>
  )
}

// ─── SCROLL REVEAL ────────────────────────────────────────────────────────────
function ScrollReveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } }, { threshold: 0.08 })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`, ...style,
    }}>{children}</div>
  )
}

// ─── ANIMATED COUNTER ─────────────────────────────────────────────────────────
function AnimatedCounter({ target, suffix = '', duration = 1800 }) {
  const [count, setCount] = useState(0)
  const ref = useRef(null); const started = useRef(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true
        const start = Date.now()
        const tick = () => {
          const p = Math.min((Date.now() - start) / duration, 1)
          setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target))
          if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      }
    }, { threshold: 0.5 })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [target, duration])
  return <span ref={ref}>{count}{suffix}</span>
}

// ─── GLOW BUTTON ─────────────────────────────────────────────────────────────
function GlowButton({ onClick, disabled, children, style = {}, className = '' }) {
  const [ripples, setRipples] = useState([])
  const handleClick = (e) => {
    if (disabled) return
    const rect = e.currentTarget.getBoundingClientRect()
    const id = Date.now()
    setRipples(r => [...r, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }])
    setTimeout(() => setRipples(r => r.filter(rp => rp.id !== id)), 700)
    onClick && onClick(e)
  }
  return (
    <button className={className} disabled={disabled} onClick={handleClick} style={{ position: 'relative', overflow: 'hidden', ...style }}>
      {ripples.map(rp => (
        <span key={rp.id} style={{ position: 'absolute', left: rp.x - 60, top: rp.y - 60, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', animation: 'rippleOut 0.7s ease-out forwards', pointerEvents: 'none' }} />
      ))}
      {children}
    </button>
  )
}

// ─── HERO DEMO ANIMATION ──────────────────────────────────────────────────────
const DEMO_SCENES = [
  { id: 'report', label: 'Bug Report', icon: '📋', color: '#4a9eff' },
  { id: 'stack',  label: 'Stack Trace', icon: '📟', color: '#4ade80' },
  { id: 'attach', label: 'Attachment',  icon: '📎', color: '#7c3aed' },
  { id: 'analyze',label: 'Analyzing',   icon: '🧠', color: '#4a9eff' },
  { id: 'result', label: 'Result',      icon: '✅', color: '#4ade80' },
]
const BUG_TEXT   = `User reports: Login button not working after latest deployment. Users get "500 Internal Server Error" on /api/auth/login endpoint.`
const STACK_TEXT = `TypeError: Cannot read properties of undefined\n  at AuthService.validateUser (auth.service.ts:47)\n  at async LocalStrategy.validate (local.strategy.ts:23)\n  at async PassportStrategy.authenticate`
const RESULT_STEPS = ['Check JWT expiry config in auth.module.ts','Add null guard in validateUser()','Redeploy with fix']

function HeroDemoAnimation() {
  const [scene, setScene] = useState(0)
  const [typed, setTyped] = useState('')
  const [stackTyped, setStackTyped] = useState('')
  const [showAttach, setShowAttach] = useState(false)
  const [analyzeProgress, setAnalyzeProgress] = useState(0)
  const [showResult, setShowResult] = useState(false)
  const canvasRef = useRef(null); const animRef = useRef(null)

  useEffect(() => {
    const durations = [3200, 2800, 1800, 3500, 3500]
    const t = setTimeout(() => setScene(s => (s + 1) % DEMO_SCENES.length), durations[scene])
    return () => clearTimeout(t)
  }, [scene])

  useEffect(() => {
    setTyped(''); setStackTyped(''); setShowAttach(false); setAnalyzeProgress(0); setShowResult(false)
  }, [scene])

  useEffect(() => {
    if (scene !== 0) return
    let i = 0; const t = setInterval(() => { i++; setTyped(BUG_TEXT.slice(0, i)); if (i >= BUG_TEXT.length) clearInterval(t) }, 28)
    return () => clearInterval(t)
  }, [scene])

  useEffect(() => {
    if (scene !== 1) return
    let i = 0; const t = setInterval(() => { i++; setStackTyped(STACK_TEXT.slice(0, i)); if (i >= STACK_TEXT.length) clearInterval(t) }, 22)
    return () => clearInterval(t)
  }, [scene])

  useEffect(() => {
    if (scene !== 2) return
    const t = setTimeout(() => setShowAttach(true), 400); return () => clearTimeout(t)
  }, [scene])

  useEffect(() => {
    if (scene !== 3) return
    let p = 0; const t = setInterval(() => { p += 1.5; setAnalyzeProgress(Math.min(p, 100)); if (p >= 100) clearInterval(t) }, 40)
    return () => clearInterval(t)
  }, [scene])

  useEffect(() => {
    if (scene !== 3) { if (animRef.current) cancelAnimationFrame(animRef.current); return }
    const canvas = canvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d'); canvas.width = 340; canvas.height = 110
    const nodes = Array.from({ length: 12 }, () => ({ x: 20 + Math.random() * 300, y: 15 + Math.random() * 80, r: Math.random() * 3 + 2, pulse: Math.random() * Math.PI * 2, speed: Math.random() * 0.04 + 0.02 }))
    const conns = []; nodes.forEach((a, i) => nodes.forEach((b, j) => { if (i < j && Math.hypot(a.x - b.x, a.y - b.y) < 80) conns.push([i, j]) }))
    let frame = 0
    const draw = () => {
      ctx.clearRect(0, 0, 340, 110); frame++
      conns.forEach(([i, j]) => {
        const a = nodes[i], b = nodes[j], pulse = Math.sin(frame * 0.05 + a.pulse)
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y)
        ctx.strokeStyle = `rgba(74,158,255,${0.06 + 0.1 * pulse})`; ctx.lineWidth = 1; ctx.stroke()
        const t2 = (Math.sin(frame * 0.03 + i) + 1) / 2
        ctx.beginPath(); ctx.arc(a.x + (b.x - a.x) * t2, a.y + (b.y - a.y) * t2, 2.5, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(74,222,128,0.75)'; ctx.fill()
      })
      nodes.forEach(n => {
        const pulse = Math.sin(frame * n.speed + n.pulse)
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r + pulse * 1.5, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(74,158,255,${0.45 + 0.45 * pulse})`; ctx.fill()
      })
      animRef.current = requestAnimationFrame(draw)
    }
    draw(); return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [scene])

  useEffect(() => {
    if (scene !== 4) return
    const t = setTimeout(() => setShowResult(true), 300); return () => clearTimeout(t)
  }, [scene])

  const cur = DEMO_SCENES[scene]
  return (
    <div style={{ background: 'rgba(5,8,15,0.94)', border: '1px solid rgba(74,158,255,0.22)', borderRadius: 20, padding: 22, backdropFilter: 'blur(20px)', boxShadow: '0 28px 80px rgba(0,0,0,0.55)', minHeight: 370, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6 }}>{['#ef4444','#f59e0b','#4ade80'].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}</div>
        <span style={{ fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: '0.08em' }}>SYSNERVE LIVE DEMO</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1.5s infinite', boxShadow: '0 0 6px #4ade80' }} />
          <span style={{ fontSize: 10, color: '#4ade80', fontWeight: 700 }}>LIVE</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 16 }}>
        {DEMO_SCENES.map((s, i) => (
          <div key={s.id} style={{ flex: 1, height: 3, borderRadius: 2, background: i === scene ? s.color : i < scene ? '#334155' : '#1e293b', transition: 'all 0.4s', boxShadow: i === scene ? `0 0 8px ${s.color}` : 'none' }} />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 15 }}>{cur.icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: cur.color, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Step {scene + 1}: {cur.label}</span>
      </div>

      {scene === 0 && (
        <div style={{ animation: 'fadeUp 0.4s ease' }}>
          <div style={{ background: '#000', border: '1px solid #1e3a5f', borderRadius: 10, padding: 12, minHeight: 95, fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0', lineHeight: 1.7 }}>
            {typed}<span style={{ borderRight: '2px solid #4a9eff', animation: 'blink 0.8s infinite', marginLeft: 1 }} />
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['Login','500 Error','/api/auth','Production'].map(tag => (
              <span key={tag} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.25)', color: '#4a9eff', fontWeight: 600 }}>{tag}</span>
            ))}
          </div>
        </div>
      )}

      {scene === 1 && (
        <div style={{ animation: 'fadeUp 0.4s ease' }}>
          <div style={{ background: '#000', border: '1px solid #1e3a5f', borderRadius: 10, padding: 12, minHeight: 95, fontFamily: 'monospace', fontSize: 12, color: '#4ade80', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {stackTyped}<span style={{ borderRight: '2px solid #4ade80', animation: 'blink 0.8s infinite', marginLeft: 1 }} />
          </div>
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>⚠ TypeError detected at line 47</span>
          </div>
        </div>
      )}

      {scene === 2 && (
        <div style={{ animation: 'fadeUp 0.4s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[{ icon: '📸', name: 'screenshot.png', size: '2.1 MB · PNG', color: '#4ade80', delay: 0 }, { icon: '📄', name: 'server.log', size: '856 KB · LOG', color: '#7c3aed', delay: 300 }].map(f => (
              <div key={f.name} style={{ border: `2px dashed ${showAttach ? f.color : '#1e3a5f'}`, borderRadius: 10, padding: 16, textAlign: 'center', background: showAttach ? f.color + '08' : 'rgba(0,0,0,0.3)', transition: `all 0.5s ease ${f.delay}ms` }}>
                <div style={{ fontSize: 24, marginBottom: 5 }}>{f.icon}</div>
                <div style={{ fontSize: 11, color: showAttach ? f.color : '#475569', fontWeight: 600, transition: `color 0.5s ${f.delay}ms` }}>{showAttach ? `✓ ${f.name}` : f.icon === '📸' ? 'Upload Screenshot' : 'Upload Log File'}</div>
                {showAttach && <div style={{ fontSize: 10, color: '#334155', marginTop: 3 }}>{f.size}</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, fontSize: 12, color: '#a78bfa' }}>
            {showAttach ? '✓ 2 attachments ready for AI analysis' : 'Waiting for attachments...'}
          </div>
        </div>
      )}

      {scene === 3 && (
        <div style={{ animation: 'fadeUp 0.4s ease' }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: '#4a9eff', fontWeight: 700 }}>AI Engine Processing...</span>
              <span style={{ fontSize: 11, color: '#4a9eff', fontWeight: 700 }}>{Math.round(analyzeProgress)}%</span>
            </div>
            <div style={{ height: 4, background: '#0f1e35', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#1d4ed8,#4a9eff)', width: analyzeProgress + '%', transition: 'width 0.1s', boxShadow: '0 0 12px rgba(74,158,255,0.6)' }} />
            </div>
          </div>
          <canvas ref={canvasRef} style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block' }} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 8 }}>
            {['Reading','Analyzing','Matching','Generating'].map((s, i) => (
              <div key={s} style={{ fontSize: 10, color: '#475569', textAlign: 'center' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: analyzeProgress > i * 25 ? '#4a9eff' : '#1e293b', margin: '0 auto 3px', boxShadow: analyzeProgress > i * 25 ? '0 0 8px #4a9eff' : 'none', transition: 'all 0.3s' }} />
                {s}
              </div>
            ))}
          </div>
        </div>
      )}

      {scene === 4 && (
        <div style={{ animation: 'fadeUp 0.4s ease' }}>
          {showResult ? (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, fontWeight: 800 }}>🔴 REAL BUG</span>
                <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 11, fontWeight: 700 }}>CRITICAL</span>
                <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', color: '#4ade80', fontSize: 11, fontWeight: 700 }}>97% confidence</span>
              </div>
              <div style={{ background: '#000', border: '1px solid #1e3a5f', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 4 }}>ROOT CAUSE</div>
                <div style={{ fontSize: 11, color: '#4ade80', lineHeight: 1.7, fontFamily: 'monospace' }}>Null reference in AuthService.validateUser() — user object undefined when JWT token expires.</div>
              </div>
              <div style={{ background: '#000', border: '1px solid #1e3a5f', borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 9, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 5 }}>ACTION PLAN</div>
                {RESULT_STEPS.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 4, animation: `fadeUp 0.4s ease ${i * 100}ms both` }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 11, color: '#c9d3e0', lineHeight: 1.5 }}>{step}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)', borderRadius: 7, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 700 }}>⏱ Time Saved: 3-5 hours</span>
                <span style={{ fontSize: 10, color: '#334155' }}>Senior Dev Level</span>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 28 }}>
              <div style={{ width: 26, height: 26, border: '2px solid #4a9eff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 10px' }} />
              <div style={{ color: '#475569', fontSize: 12 }}>Preparing result...</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── HOW IT WORKS — CENTERED LAYOUT ──────────────────────────────────────────
function HowItWorksDashboard() {
  const [activeStep, setActiveStep] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setActiveStep(s => (s + 1) % 3), 2800)
    return () => clearInterval(t)
  }, [])
  const steps = [
    { icon: '📋', title: 'Add Details', desc: 'Paste bug report, stack trace, or upload screenshot/log file.', color: '#4a9eff' },
    { icon: '🧠', title: 'AI Analyzes', desc: 'SysNerve analyzes everything with deep intelligence instantly.', color: '#7c3aed' },
    { icon: '✅', title: 'Get Answers', desc: 'Get verdict, root cause, severity, and action plan instantly.', color: '#4ade80' },
  ]
  const mockResults = [
    { label: 'VERDICT', value: 'REAL BUG', color: '#ef4444' },
    { label: 'SEVERITY', value: 'CRITICAL', color: '#ef4444' },
    { label: 'CONFIDENCE', value: '97%', color: '#fff' },
    { label: 'TIME SAVED', value: '3-5 hrs', color: '#4ade80' },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 48, width: '100%' }}>
<div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', justifyContent: 'center', width: '100%', maxWidth: 760, flexWrap: 'wrap' }}>

        {steps.map((step, i) => (
          <div key={step.title} style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
            <div onClick={() => setActiveStep(i)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, cursor: 'pointer', opacity: activeStep === i ? 1 : 0.42, transition: 'all 0.4s ease', flex: 1, padding: '0 8px' }}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: activeStep === i ? `linear-gradient(135deg,#1d4ed8,#4a9eff)` : '#050810',
                  border: `2px solid ${activeStep === i ? '#4a9eff' : '#1e3a5f'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, transition: 'all 0.4s',
                  boxShadow: activeStep === i ? '0 0 24px rgba(74,158,255,0.45)' : 'none',
                }}>{step.icon}</div>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 56, height: 56, borderRadius: '50%', border: activeStep === i ? '1px solid rgba(74,158,255,0.25)' : 'none', animation: activeStep === i ? 'ping 2s ease-in-out infinite' : 'none', pointerEvents: 'none' }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: activeStep === i ? step.color : '#475569', letterSpacing: '0.08em', marginBottom: 4 }}>STEP {i + 1}</div>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 6px', color: activeStep === i ? '#fff' : '#94a3b8', fontFamily: "'Syne',sans-serif" }}>{step.title}</h3>
                <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.6, maxWidth: 160 }}>{step.desc}</p>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flexShrink: 0, width: 48, display: 'flex', alignItems: 'center', paddingTop: 28 }}>
                <div style={{ height: 2, width: '100%', background: activeStep > i ? 'linear-gradient(90deg,#4a9eff,#4a9eff44)' : '#1e3a5f', borderRadius: 2, transition: 'background 0.5s' }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ width: '100%', maxWidth: 600, background: 'rgba(5,8,15,0.94)', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 20, padding: 28, boxShadow: '0 24px 70px rgba(0,0,0,0.55)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 5 }}>{['#ef4444','#f59e0b','#4ade80'].map(c => <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />)}</div>
          <span style={{ fontSize: 10, color: '#475569', fontWeight: 700 }}>SYSNERVE ANALYZER</span>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1.5s infinite', boxShadow: '0 0 6px #4ade80' }} />
        </div>

        <div style={{ opacity: activeStep === 0 ? 1 : 0.28, transition: 'opacity 0.5s', marginBottom: 14 }}>
          <div style={{ background: '#000', border: `1px solid ${activeStep === 0 ? '#4a9eff44' : '#1e3a5f'}`, borderRadius: 10, padding: 12, fontSize: 12, color: '#64748b', fontFamily: 'monospace', lineHeight: 1.7, transition: 'border-color 0.4s' }}>
            {activeStep === 0 ? <span>Login fails after deployment... <span style={{ borderRight: '2px solid #4a9eff', animation: 'blink 0.8s infinite' }} /></span> : 'Bug report pasted ✓'}
          </div>
        </div>

        <div style={{ opacity: activeStep === 1 ? 1 : 0.28, transition: 'opacity 0.5s', marginBottom: 14 }}>
          <div style={{ background: '#000', border: `1px solid ${activeStep === 1 ? '#7c3aed44' : '#1e3a5f'}`, borderRadius: 10, padding: 12, transition: 'border-color 0.4s' }}>
            <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, marginBottom: 8 }}>AI PROCESSING</div>
            <div style={{ height: 4, background: '#0f1e35', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#1d4ed8,#7c3aed)', width: activeStep === 1 ? '100%' : '0%', transition: 'width 2.5s ease', boxShadow: '0 0 10px rgba(124,58,237,0.5)' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
              {['Reading','Analyzing','Matching','Done'].map((s) => (
                <span key={s} style={{ fontSize: 9, color: activeStep === 1 ? '#7c3aed' : '#334155', transition: 'color 0.3s' }}>{s}</span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ opacity: activeStep === 2 ? 1 : 0.28, transition: 'opacity 0.5s' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            {mockResults.map(r => (
              <div key={r.label} style={{ background: '#000', border: `1px solid ${activeStep === 2 ? r.color + '33' : '#1e3a5f'}`, borderRadius: 8, padding: '10px 12px', transition: 'border-color 0.4s' }}>
                <div style={{ fontSize: 8, color: '#475569', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 4 }}>{r.label}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: activeStep === 2 ? r.color : '#334155', transition: 'color 0.4s', fontFamily: "'Syne',sans-serif" }}>{r.value}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#000', border: `1px solid ${activeStep === 2 ? '#4ade8033' : '#1e3a5f'}`, borderRadius: 8, padding: '10px 12px', transition: 'border-color 0.4s' }}>
            <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 5 }}>ROOT CAUSE</div>
            <div style={{ fontSize: 12, color: activeStep === 2 ? '#4ade80' : '#334155', fontFamily: 'monospace', lineHeight: 1.6, transition: 'color 0.4s' }}>Null ref in validateUser() — JWT expired during active session.</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── TYPING TEXT ──────────────────────────────────────────────────────────────
function TypingText({ texts }) {
  const [displayed, setDisplayed] = useState('')
  const [idx, setIdx] = useState(0); const [charIdx, setCharIdx] = useState(0); const [deleting, setDeleting] = useState(false)
  useEffect(() => {
    const current = texts[idx]; let t
    if (!deleting && charIdx < current.length) t = setTimeout(() => setCharIdx(c => c + 1), 55)
    else if (!deleting && charIdx === current.length) t = setTimeout(() => setDeleting(true), 2200)
    else if (deleting && charIdx > 0) t = setTimeout(() => setCharIdx(c => c - 1), 28)
    else { setDeleting(false); setIdx(i => (i + 1) % texts.length) }
    setDisplayed(current.slice(0, charIdx)); return () => clearTimeout(t)
  }, [charIdx, deleting, idx, texts])
  return (
    <span style={{ color: '#4a9eff' }}>
      {displayed}<span style={{ borderRight: '2px solid #4a9eff', animation: 'blink 1s infinite', marginLeft: 1 }} />
    </span>
  )
}

// ─── AI BRAIN ────────────────────────────────────────────────────────────────
function AIBrain({ active }) {
  const canvasRef = useRef(null)
  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current; const ctx = canvas.getContext('2d')
    canvas.width = 320; canvas.height = 160
    const nodes = Array.from({ length: 16 }, () => ({ x: 20 + Math.random() * 280, y: 20 + Math.random() * 120, r: Math.random() * 4 + 2, pulse: Math.random() * Math.PI * 2, speed: Math.random() * 0.05 + 0.02 }))
    const conns = []; nodes.forEach((a, i) => nodes.forEach((b, j) => { if (i < j && Math.hypot(a.x - b.x, a.y - b.y) < 90) conns.push([i, j]) }))
    let frame = 0, id
    const draw = () => {
      ctx.clearRect(0, 0, 320, 160); frame++
      conns.forEach(([i, j]) => {
        const a = nodes[i], b = nodes[j], pulse = Math.sin(frame * 0.05 + a.pulse)
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y)
        ctx.strokeStyle = `rgba(74,158,255,${0.08 + 0.12 * pulse})`; ctx.lineWidth = 1; ctx.stroke()
        const t2 = (Math.sin(frame * 0.03 + i) + 1) / 2
        ctx.beginPath(); ctx.arc(a.x + (b.x - a.x) * t2, a.y + (b.y - a.y) * t2, 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(74,222,128,0.7)'; ctx.fill()
      })
      nodes.forEach(n => {
        const pulse = Math.sin(frame * n.speed + n.pulse)
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r + pulse * 1.5, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(74,158,255,${0.5 + 0.5 * pulse})`; ctx.fill()
      })
      id = requestAnimationFrame(draw)
    }
    draw(); return () => cancelAnimationFrame(id)
  }, [active])
  if (!active) return null
  return (
    <div style={{ background: '#020508', border: '1px solid #1e3a5f', borderRadius: 12, padding: 16, textAlign: 'center', marginBottom: 8, animation: 'fadeUp 0.4s ease' }}>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: '#4a9eff', fontWeight: 600 }}>🧠 Senior Developer Level Debugging in progress...</p>
      <canvas ref={canvasRef} style={{ borderRadius: 8, display: 'block', margin: '0 auto' }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 10 }}>
        {['Reading Report','Code Analysis','Pattern Match','Generating Fix'].map((s, i) => (
          <div key={s} style={{ fontSize: 10, color: '#4a5568', textAlign: 'center' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4a9eff', margin: '0 auto 4px', animation: `pulse 1s ${i * 0.25}s ease-in-out infinite` }} />
            {s}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={() => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }) }}
      style={{ background: copied ? '#4ade8018' : '#0a0c10', border: `1px solid ${copied ? '#4ade8044' : '#1e2d45'}`, borderRadius: 6, color: copied ? '#4ade80' : '#64748b', fontSize: 11, fontWeight: 600, padding: '4px 10px', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {copied ? '✓ Copied!' : `📋 ${label}`}
    </button>
  )
}

// ─── SEVERITY BADGE ──────────────────────────────────────────────────────────
function SeverityBadge({ severity }) {
  const isCritical = severity.includes('CRITICAL'), isHigh = severity.includes('HIGH'), isMedium = severity.includes('MEDIUM')
  const color = isCritical ? '#ef4444' : isHigh ? '#f97316' : isMedium ? '#f59e0b' : '#4ade80'
  const bg = isCritical ? '#ef444418' : isHigh ? '#f9731618' : isMedium ? '#f59e0b18' : '#4ade8018'
  const dot = isCritical ? '🔴' : isHigh ? '🟠' : isMedium ? '🟡' : '🟢'
  const anim = isCritical ? 'criticalBlink 0.6s ease-in-out infinite' : isHigh ? 'highPulse 1s ease-in-out infinite' : 'pulse 2s infinite'
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, border: `1px solid ${color}44`, borderRadius: 8, padding: '6px 12px' }}>
      <span style={{ fontSize: 14, animation: anim, display: 'inline-block' }}>{dot}</span>
      <span style={{ fontSize: 12, fontWeight: 800, color, letterSpacing: '0.08em' }}>{severity}</span>
    </div>
  )
}

// ─── RESULT DISPLAY ──────────────────────────────────────────────────────────
function ResultDisplay({ result, analysisTime }) {
  const [techOpen, setTechOpen] = useState(false)
  if (!result) return null
  const isRealBug = result.includes('REAL BUG'), isUserError = result.includes('USER MISUNDERSTANDING'), isInfra = result.includes('INFRASTRUCTURE')
  const verdictColor = isRealBug ? '#ef4444' : isUserError ? '#f59e0b' : isInfra ? '#8b5cf6' : '#4a9eff'
  const timeSaved = isRealBug ? '2-4 hours' : isUserError ? '3-5 hours' : isInfra ? '1-3 hours' : '1-2 hours'
  const getSection = (key) => { const m = result.match(new RegExp(key + ':\\s*([\\s\\S]*?)(?=\\n[A-Z][A-Z ]+:|$)')); return m ? m[1].trim() : '' }
  const confidence = getSection('CONFIDENCE'), severity = getSection('SEVERITY'), explanation = getSection('EXPLANATION')
  const rootCause = getSection('ROOT CAUSE'), actionPlan = getSection('ACTION PLAN')
  const imageAnalysis = getSection('IMAGE ANALYSIS'), previousPattern = getSection('PREVIOUS PATTERN'), quickTip = getSection('QUICK TIP')
  const actionSteps = actionPlan.split('\n').filter(l => l.trim()).map(s => s.replace(/^\d+\.\s*/, ''))
  const rc = (bc) => ({ background: '#05080f', border: `1px solid ${bc || '#0f1e35'}`, borderRadius: 14, overflow: 'hidden', marginBottom: 10 })
  const sl = (c) => ({ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: c || '#475569' })
  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'resultIn 0.5s ease' }}>
      <div style={rc(verdictColor + '44')}>
        <div style={{ height: 3, background: `linear-gradient(90deg,${verdictColor},${verdictColor}55,transparent)` }} />
        <div style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: 10, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}> SysNerve Analysis Report · {new Date().toLocaleString()}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#f1f5f9', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 12 }}>
            {isUserError ? '❌ NOT A BUG' : isRealBug ? '🐛 Real Bug Found' : isInfra ? '🖥️ Infrastructure Issue' : '🔍 Analysis Complete'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {severity && <SeverityBadge severity={severity} />}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#4a9eff0a', border: '1px solid #4a9eff22', borderRadius: 8, padding: '6px 12px' }}>
              <span style={{ fontSize: 12, color: '#4a9eff', fontWeight: 700 }}>⏱ {analysisTime}s</span>
            </div>
            {confidence && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: verdictColor + '0a', border: `1px solid ${verdictColor}33`, borderRadius: 8, padding: '6px 12px' }}><span style={{ fontSize: 12, color: verdictColor, fontWeight: 700 }}>{confidence} confidence</span></div>}
          </div>
          {isUserError && <div style={{ background: '#f59e0b08', border: '1px solid #f59e0b33', borderRadius: 10, padding: '11px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}><span style={{ fontSize: 16 }}>⚠️</span><div><div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', marginBottom: 3 }}>This is NOT a system bug</div><div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>No code changes needed. This is a user input or configuration issue.</div></div></div>}
          <div style={{ background: '#4ade8008', border: '1px solid #4ade8022', borderRadius: 10, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>⏱</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>Estimated Time Saved: {timeSaved}</span>
            <span style={{ fontSize: 11, color: '#334155', marginLeft: 'auto' }}>Senior Developer Level</span>
          </div>
        </div>
      </div>
      {explanation && <div style={{ ...rc('#4ade8033'), borderLeft: '3px solid #4ade80' }}><div style={{ padding: '12px 18px', borderBottom: '1px solid #0f1e35', display: 'flex', alignItems: 'center', gap: 8 }}><img src="/logo.png" style={{ height: 22, width: 22, borderRadius: 4 }} alt="" /><span style={sl('#4ade80')}>Quick Summary</span></div><div style={{ padding: '14px 18px', fontSize: 14, color: '#c9d3e0', lineHeight: 1.8 }}>{explanation}</div></div>}
      {actionPlan && (
        <div style={{ ...rc('#4a9eff33'), borderLeft: '3px solid #4a9eff' }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #0f1e35', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>🔧</span><span style={sl('#4a9eff')}>Action Plan</span></div>
            <CopyBtn text={actionSteps.join('\n')} label="Copy Fix Steps" />
          </div>
          <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actionSteps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: '#c9d3e0', lineHeight: 1.7, paddingTop: 2 }}>{step}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {quickTip && <div style={{ background: '#4a9eff08', border: '1px solid #4a9eff22', borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}><span style={{ fontSize: 16 }}>💡</span><div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}><strong style={{ color: '#4a9eff' }}>Quick Tip: </strong>{quickTip}</div></div>}
      <div style={{ ...rc('#33445533'), borderLeft: '3px solid #334155' }}>
        <button onClick={() => setTechOpen(!techOpen)} style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '13px 18px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
          <span style={{ fontSize: 15 }}>🔧</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: techOpen ? '#4a9eff' : '#64748b' }}>Technical Details</span>
          <span style={{ fontSize: 11, color: '#334155', marginLeft: 4 }}>Root Cause · Evidence · Error</span>
          <span style={{ marginLeft: 'auto', color: '#475569', transition: 'transform 0.3s', transform: techOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </button>
        {techOpen && (
          <div style={{ borderTop: '1px solid #0f1e35', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {rootCause && <div><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}><span style={sl('#4ade80')}>🎯 Root Cause</span><CopyBtn text={rootCause} label="Copy Error" /></div><div style={{ fontSize: 13, color: '#4ade80', lineHeight: 1.8, background: '#000', borderRadius: 8, padding: '12px 14px', border: '1px solid #4ade8022', fontFamily: 'monospace' }}>{rootCause}</div></div>}
            {imageAnalysis && imageAnalysis.toLowerCase() !== 'skip' && !imageAnalysis.toLowerCase().includes('no image') && <div><div style={{ marginBottom: 6 }}><span style={sl('#7c3aed')}>📸 Image Analysis</span></div><div style={{ fontSize: 13, color: '#c9d3e0', lineHeight: 1.8, background: '#000', borderRadius: 8, padding: '12px 14px', border: '1px solid #7c3aed22' }}>{imageAnalysis}</div></div>}
            {previousPattern && <div><div style={{ marginBottom: 6 }}><span style={sl('#f59e0b')}>🔄 Previous Pattern</span></div><div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.8, background: '#000', borderRadius: 8, padding: '12px 14px', border: '1px solid #f59e0b22' }}>{previousPattern}</div></div>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px' }}>
        <span style={{ fontSize: 11, color: '#1e293b' }}>Generated by SysNerve AI Engine</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#4a9eff' }}>⚡ SysNerve</span>
      </div>
    </div>
  )
}

// ─── HISTORY ITEM ────────────────────────────────────────────────────────────
function HistoryItem({ item, onSelect }) {
  const [expanded, setExpanded] = useState(false)
  const isRealBug = item.verdict?.includes('REAL BUG'), isUserError = item.verdict?.includes('USER MISUNDERSTANDING')
  const color = isRealBug ? '#ef4444' : isUserError ? '#f59e0b' : '#8b5cf6'
  const emoji = isRealBug ? '🐛' : isUserError ? '👤' : '🖥️'
  return (
    <div style={{ marginBottom: 8 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: expanded ? '#0a0c16' : '#000', borderRadius: expanded ? '8px 8px 0 0' : 8, border: `1px solid ${expanded ? color + '44' : '#0f1e35'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
        <span style={{ fontSize: 18 }}>{emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.bug_report?.slice(0, 50) || 'Bug report'}...</div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{new Date(item.created_at).toLocaleDateString()} · <span style={{ color }}>{item.verdict}</span>{item.has_image && <span style={{ marginLeft: 6, color: '#7c3aed' }}>📸</span>}{item.has_file && <span style={{ marginLeft: 4, color: '#4a9eff' }}>📄 {item.file_name}</span>}</div>
        </div>
        <div style={{ fontSize: 11, color, fontWeight: 700, background: color + '11', padding: '2px 8px', borderRadius: 20, border: `1px solid ${color}33` }}>{item.severity}</div>
        <span style={{ fontSize: 10, color: '#475569', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', display: 'inline-block', transition: 'transform 0.3s' }}>▼</span>
      </div>
      {expanded && (
        <div style={{ background: '#05080f', border: `1px solid ${color}33`, borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 12 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6, marginBottom: 10, background: '#000', borderRadius: 6, padding: '8px 10px', border: '1px solid #0f1e35' }}>{item.bug_report || 'Not available'}</div>
          {item.full_result && (
            <>
              <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.8, background: '#000', borderRadius: 6, padding: '8px 10px', border: '1px solid #0f1e35', whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto' }}>{item.full_result.slice(0, 400)}...</div>
              <button onClick={() => onSelect && onSelect(item)} style={{ width: '100%', marginTop: 10, background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px', cursor: 'pointer' }}>⚡ Load Full Report</button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── WAITLIST MODAL ───────────────────────────────────────────────────────────
function WaitlistModal({ onClose }) {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = () => {
    if (!email.includes('@')) return
    setLoading(true)
    setTimeout(() => { setLoading(false); setSubmitted(true) }, 1200)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#05080f', border: '1px solid #1e3a5f', borderRadius: 24, padding: 40, textAlign: 'center', animation: 'modalIn 0.3s ease', maxWidth: 420, width: '90%', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18 }}>✕</button>
        {!submitted ? (
          <>
            <div style={{ fontSize: 44, marginBottom: 16, animation: 'float 2s ease-in-out infinite' }}>🚀</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 8px', fontFamily: "'Syne',sans-serif" }}>Join the Waitlist</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 6px', lineHeight: 1.6 }}>SysNerve is launching soon. Get early access + 30 days free on any plan.</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, marginTop: 20, background: '#4ade8008', border: '1px solid #4ade8022', borderRadius: 10, padding: '10px 14px' }}>
              {['30 days free','Priority access','Founding price lock'].map(b => (
                <span key={b} style={{ fontSize: 11, color: '#4ade80', fontWeight: 700 }}>✓ {b}</span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <input
                type="email" placeholder="your@email.com" value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={{ flex: 1, background: '#000', border: '1px solid #1e3a5f', borderRadius: 10, color: '#e2e8f0', fontSize: 14, padding: '12px 14px', outline: 'none', fontFamily: 'inherit' }}
              />
              <GlowButton onClick={handleSubmit} disabled={loading}
                style={{ background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px 20px', cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                {loading ? '...' : 'Join →'}
              </GlowButton>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 8px', fontFamily: "'Syne',sans-serif", color: '#4ade80' }}>You're on the list!</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px', lineHeight: 1.6 }}>We'll email <strong style={{ color: '#e2e8f0' }}>{email}</strong> when SysNerve launches. Expect early access + 30 days free.</p>
            <button onClick={onClose} style={{ background: 'transparent', border: '1px solid #1e3a5f', borderRadius: 10, color: '#64748b', fontSize: 13, padding: '10px 24px', cursor: 'pointer' }}>Close</button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── PRICING ─────────────────────────────────────────────────────────────────
const packages = [
  { name: 'Starter', price: '$29', period: '/month', desc: 'Perfect for small dev teams dealing with frequent fake bug reports.', highlight: false, cta: 'Get Started', ctaAction: 'signin', features: ['Up to 5 team members','100 analyses / month','Bug Report + Stack Trace','NOT A BUG detection','Bug history (30 days)','Email support'] },
  { name: 'Team', price: '$79', period: '/month', desc: 'Full power for growing engineering teams with complex systems.', highlight: true, badge: 'Most Popular', cta: 'Join Waitlist', ctaAction: 'waitlist', features: ['Unlimited team members','Unlimited analyses','Screenshot & image analysis','Log file analysis','GitHub integration','Jira / Linear integration','Slack / Teams alerts','Priority support'] },
  { name: 'Enterprise', price: '$199', period: '/month', desc: 'Live infrastructure monitoring for production-grade systems.', highlight: false, cta: 'Join Waitlist', ctaAction: 'waitlist', features: ['Everything in Team','Live server monitoring','DB replication checks','Cron job monitoring','Incident post-mortem reports','Deployment rollback suggestions','Custom integrations','Dedicated support'] },
]

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    name: 'Marcus Chen',
    role: 'Senior Backend Engineer',
  
    stars: 5,
    comment: "SysNerve flagged a null reference in our payment pipeline that had slipped past three code reviews. What would've taken my team half a day took SysNerve 18 seconds. Absolute game-changer.",
    avatar: 'MC',
    avatarColor: '#4a9eff',
  },
  {
    name: 'Priya Nair',
    role: 'CTO',
    
    stars: 5,
    comment: "The NOT A BUG detection alone saved us from a week of wasted engineering time. Our junior devs were escalating user config issues as critical bugs. SysNerve stopped that cold.",
    avatar: 'PN',
    avatarColor: '#4ade80',
  },
  {
    name: 'Jordan Walsh',
    role: 'Lead Developer',
    
    stars: 5,
    comment: "I was skeptical about AI debugging tools but SysNerve proved me wrong. It correctly identified a race condition in our edge runtime within seconds, including the exact file and line number.",
    avatar: 'JW',
    avatarColor: '#7c3aed',
  },
  {
    name: 'Aisha Okonkwo',
    role: 'Engineering Manager',
    
    stars: 4,
    comment: "Rolled it out to a team of 12 engineers last quarter. Bug triage time dropped by 60%. The multilingual support was a bonus — our global team submits reports in their native language now.",
    avatar: 'AO',
    avatarColor: '#f59e0b',
  },
]

function StarRating({ count }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ fontSize: 14, color: i <= count ? '#f59e0b' : '#1e293b' }}>★</span>
      ))}
    </div>
  )
}

function Testimonials() {
  const [active, setActive] = useState(0)
  const [fading, setFading] = useState(false)
  const timerRef = useRef(null)

  const goTo = (idx) => {
    if (idx === active) return
    setFading(true)
    setTimeout(() => {
      setActive(idx)
      setFading(false)
    }, 280)
  }

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setFading(true)
      setTimeout(() => {
        setActive(a => (a + 1) % TESTIMONIALS.length)
        setFading(false)
      }, 280)
    }, 4000)
    return () => clearInterval(timerRef.current)
  }, [])

  const t = TESTIMONIALS[active]

  return (
    <section style={{ padding: '80px 5%', position: 'relative', zIndex: 1 }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <ScrollReveal>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: 11, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>What Engineers Say</div>
            
<h2 style={{ fontSize: 38, fontWeight: 900, margin: '0 0 14px' }}>Trusted by the People <span style={{ color: '#4a9eff' }}>Behind the Code</span></h2>
<p style={{ color: '#475569', fontSize: 15, margin: 0 }}>Real Team. Real Bugs. Real Time Saved.</p>
          </div>
        </ScrollReveal>

        {/* Card */}
        <div style={{
          background: '#05080f',
          border: `1px solid ${fading ? '#0f1e35' : 'rgba(74,158,255,0.28)'}`,
          borderRadius: 22,
          padding: '44px 48px',
          boxShadow: fading ? 'none' : '0 0 50px rgba(74,158,255,0.07)',
          opacity: fading ? 0 : 1,
          transform: fading ? 'translateY(8px)' : 'translateY(0)',
          transition: 'opacity 0.28s ease, transform 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative glow */}
          <div style={{
            position: 'absolute', top: -40, right: -40,
            width: 200, height: 200, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(74,158,255,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Quote mark */}
          <div style={{
            fontSize: 72, lineHeight: 1, color: 'rgba(74,158,255,0.12)',
            fontFamily: 'Georgia, serif', position: 'absolute', top: 20, left: 36,
            fontWeight: 900, userSelect: 'none',
          }}>"</div>

          <div style={{ position: 'relative' }}>
            <StarRating count={t.stars} />

            <p style={{
              fontSize: 17, color: '#c9d3e0', lineHeight: 1.85,
              margin: '20px 0 28px', fontStyle: 'italic', maxWidth: 680,
            }}>
              "{t.comment}"
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 46, height: 46, borderRadius: '50%',
                background: `linear-gradient(135deg, ${t.avatarColor}33, ${t.avatarColor}66)`,
                border: `2px solid ${t.avatarColor}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, color: t.avatarColor,
                fontFamily: "'Syne', sans-serif", flexShrink: 0,
              }}>
                {t.avatar}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#e2e8f0', fontFamily: "'Syne', sans-serif" }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{t.role}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dot navigation */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 28 }}>
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => { clearInterval(timerRef.current); goTo(i) }}
              style={{
                width: i === active ? 28 : 8,
                height: 8, borderRadius: 4,
                background: i === active ? '#4a9eff' : '#1e3a5f',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                boxShadow: i === active ? '0 0 10px rgba(74,158,255,0.6)' : 'none',
              }}
            />
          ))}
        </div>

        
    </section>
  )
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const { isSignedIn, user } = useUser()
  const [bugReport, setBugReport] = useState('')
  const [stackTrace, setStackTrace] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showSignIn, setShowSignIn] = useState(false)
  const [showWaitlist, setShowWaitlist] = useState(false)
  const [bugsAnalyzed, setBugsAnalyzed] = useState(0)
  const [loadingStep, setLoadingStep] = useState(0)
  const [attachedImage, setAttachedImage] = useState(null)
  const [attachedImageName, setAttachedImageName] = useState('')
  const [attachedFile, setAttachedFile] = useState(null)
  const [attachedFileName, setAttachedFileName] = useState('')
  const [analysisTime, setAnalysisTime] = useState(0)
  const [history, setHistory] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [navScrolled, setNavScrolled] = useState(false)
  const imageInputRef = useRef(null); const fileInputRef = useRef(null)
  const analyzerRef = useRef(null); const startTimeRef = useRef(null)

  useEffect(() => {
    document.title = 'SysNerve — AI Bug Intelligence Platform'
    const saved = localStorage.getItem('sysnerve_count'); if (saved) setBugsAnalyzed(parseInt(saved))
    const savedHistory = localStorage.getItem('sysnerve_history'); if (savedHistory) { try { setHistory(JSON.parse(savedHistory)) } catch (e) {} }
    const onScroll = () => setNavScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll); return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!loading) return
    let i = 0; const id = setInterval(() => { i = (i + 1) % 4; setLoadingStep(i) }, 900)
    return () => clearInterval(id)
  }, [loading])



  const loadingSteps = ['Reading bug report...','Analyzing attachments...','Consulting AI engine...','Generating diagnosis...']

  const handleImageUpload = (e) => {
    const file = e.target.files[0]; if (!file) return
    if (file.size > 5 * 1024 * 1024) { alert('Image max 5MB'); return }
    const reader = new FileReader()
    reader.onload = (ev) => { setAttachedImage(ev.target.result.split(',')[1]); setAttachedImageName(file.name) }
    reader.readAsDataURL(file)
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]; if (!file) return
    if (file.size > 2 * 1024 * 1024) { alert('File max 2MB'); return }
    const reader = new FileReader()
    reader.onload = (ev) => { setAttachedFile(ev.target.result); setAttachedFileName(file.name) }
    reader.readAsText(file)
  }

  const handleAnalyze = () => {
    if (!isSignedIn) { setShowSignIn(true); return }
    if (!bugReport.trim()) { alert('Please enter a bug report!'); return }
    setLoading(true); setResult(null); setLoadingStep(0)
    startTimeRef.current = Date.now()
    fetch('https://rootiq-backend.onrender.com/analyze', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bugReport, stackTrace, userEmail: user?.emailAddresses?.[0]?.emailAddress || 'anonymous', userName: user?.fullName || (user?.firstName + ' ' + user?.lastName) || user?.emailAddresses?.[0]?.emailAddress || 'Anonymous', imageBase64: attachedImage || null, fileContent: attachedFile || null, fileName: attachedFileName || null }),
    })
      .then(r => r.json())
      .then(data => {
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000)
        setAnalysisTime(elapsed)
        const res = data.error ? 'Error: ' + data.error : data.result
        setResult(res)
        if (!data.error) {
          const newCount = bugsAnalyzed + 1; setBugsAnalyzed(newCount); localStorage.setItem('sysnerve_count', newCount)
          const verdictMatch = res.match(/VERDICT:\s*(.+)/), severityMatch = res.match(/SEVERITY:\s*(.+)/)
          const newEntry = { bug_report: bugReport, verdict: verdictMatch ? verdictMatch[1].trim() : 'UNKNOWN', severity: severityMatch ? severityMatch[1].trim() : 'UNKNOWN', full_result: res, has_image: !!attachedImage, has_file: !!attachedFile, file_name: attachedFileName || null, created_at: new Date().toISOString() }
          const newHistory = [newEntry, ...history].slice(0, 15); setHistory(newHistory); localStorage.setItem('sysnerve_history', JSON.stringify(newHistory))
        }
        setLoading(false)
      })
      .catch(() => { setResult('Error connecting. Please try again.'); setLoading(false) })
  }

  const loadFromHistory = (item) => { setResult(item.full_result); setAnalysisTime(0); setShowHistory(false); analyzerRef.current?.scrollIntoView({ behavior: 'smooth' }) }

  const card = { background: '#05080f', border: '1px solid #0f1e35', borderRadius: 16, padding: 28 }

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#e2e8f0', fontFamily: "'Segoe UI', sans-serif", position: 'relative', overflowX: 'hidden' }}>
      <FloatingParticles />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&display=swap');
        *{box-sizing:border-box} html{scroll-behavior:smooth}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
        @keyframes glow{0%,100%{box-shadow:0 0 20px #2563eb55}50%{box-shadow:0 0 40px #4a9eff88}}
        @keyframes glowPulse{0%,100%{box-shadow:0 0 16px #4a9eff44}50%{box-shadow:0 0 32px #4a9eff88}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes particleRise{0%{transform:translateY(0);opacity:0.15}50%{opacity:0.22}100%{transform:translateY(-110vh);opacity:0}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes resultIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes rippleOut{from{transform:scale(0);opacity:1}to{transform:scale(3);opacity:0}}
        @keyframes criticalBlink{0%,100%{opacity:1;transform:scale(1)}40%{opacity:0.2;transform:scale(0.7)}80%{opacity:0.8;transform:scale(1.1)}}
        @keyframes highPulse{0%,100%{opacity:1}50%{opacity:0.5}}
        @keyframes navIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes modalIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
        @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes ping{0%{transform:scale(1);opacity:0.6}100%{transform:scale(1.7);opacity:0}}
        @keyframes bannerSlide{from{opacity:0;transform:translateY(-100%)}to{opacity:1;transform:translateY(0)}}
        h1,h2,h3{font-family:'Syne',sans-serif!important}
        .btn{transition:all 0.2s ease!important}
        .btn:hover{opacity:0.9;transform:translateY(-1px)!important}
        .fcard{transition:all 0.3s ease!important;cursor:default}
        .fcard:hover{border-color:#4a9eff44!important;transform:translateY(-4px)!important;box-shadow:0 16px 40px rgba(74,158,255,0.08)!important}
        .upload-btn:hover{border-color:#4a9eff!important;color:#4a9eff!important;background:#070c18!important}
        .nav-link{position:relative;color:#64748b;text-decoration:none;font-size:13px;transition:color 0.2s;white-space:nowrap}
        .nav-link::after{content:'';position:absolute;bottom:-2px;left:0;width:0;height:1px;background:#4a9eff;transition:width 0.3s}
        .nav-link:hover{color:#fff!important} .nav-link:hover::after{width:100%}
        .analyze-glow{animation:glow 2s ease-in-out infinite}
        .analyze-glow:hover{transform:translateY(-2px)!important;box-shadow:0 0 50px #4a9eff88!important}
        textarea:focus{border-color:#4a9eff!important;outline:none!important;box-shadow:0 0 0 3px #4a9eff12!important}
        ::-webkit-scrollbar{width:5px} ::-webkit-scrollbar-thumb{background:#1e2d45;border-radius:3px}
        .waitlist-btn{background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.25);color:#fff;border-radius:20px;font-size:12px;font-weight:700;padding:5px 14px;cursor:pointer;transition:all 0.2s;backdrop-filter:blur(8px)}
        .waitlist-btn:hover{background:rgba(255,255,255,0.22)!important;transform:translateY(-1px)}
        .footer-link{color:#334155;text-decoration:none;font-size:12px;transition:color 0.2s;font-weight:600}
        .footer-link:hover{color:#4a9eff}
       @media(max-width:1024px){
  .hero-grid{grid-template-columns:1fr!important}
  .analyzer-grid{grid-template-columns:1fr!important}
  .pricing-grid{grid-template-columns:1fr 1fr!important}
  .about-grid{grid-template-columns:1fr!important}
  .multilingual-grid{grid-template-columns:1fr!important}
}
@media(max-width:768px){
  .features-grid{grid-template-columns:1fr 1fr!important}
  .pricing-grid{grid-template-columns:1fr!important}
  .nav-links-d{display:none!important}
  .mobile-signin{display:flex!important}
  .stats-grid{grid-template-columns:1fr 1fr!important}
  h1{font-size:36px!important;letter-spacing:-1px!important}
  h2{font-size:28px!important}
  .section-pad{padding:60px 5%!important}
  .hiw-steps{flex-direction:column!important;align-items:center!important}
  .hiw-connector{display:none!important}
  .top-banner-text{font-size:11px!important}
}
@media(max-width:480px){
  .features-grid{grid-template-columns:1fr!important}
  .attach-grid{grid-template-columns:1fr!important}
  .pricing-grid{grid-template-columns:1fr!important}
  .stats-grid{grid-template-columns:1fr 1fr!important}
  h1{font-size:30px!important}
}
        }
      `}</style>

      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}

      {showSignIn && !isSignedIn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#05080f', border: '1px solid #1e3a5f', borderRadius: 20, padding: 40, textAlign: 'center', animation: 'modalIn 0.3s ease', maxWidth: 380, width: '90%' }}>
            <img src="/logo.png" style={{ height: 48, width: 48, borderRadius: 10, marginBottom: 16 }} alt="" />
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Sign in to SysNerve</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 24px', lineHeight: 1.6 }}>Free account. Takes 30 seconds.</p>
            <SignInButton mode="modal">
              <GlowButton className="btn analyze-glow" style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
                Sign In / Sign Up Free ⚡
              </GlowButton>
            </SignInButton>
            <button onClick={() => setShowSignIn(false)} style={{ background: 'transparent', border: 'none', color: '#475569', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── TOP BANNER ── */}
      <div style={{ background: 'linear-gradient(90deg,#050d1a,#0a1628,#050d1a)', borderBottom: '1px solid rgba(74,158,255,0.15)', padding: '11px 5%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, position: 'relative', zIndex: 200, animation: 'bannerSlide 0.6s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 8px #4ade80', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
<span className="top-banner-text" style={{ fontSize: 13, color: '#94a3b8' }}>            <span style={{ color: '#fff', fontWeight: 700 }}>SysNerve Beta</span> launching soon —
            <span style={{ color: '#4a9eff', fontWeight: 600 }}> early access includes 30 days free</span>
          </span>
        </div>
        <button className="waitlist-btn" onClick={() => setShowWaitlist(true)}>🚀 Join Waitlist →</button>
      </div>

      {/* ── NAV ── */}
<nav style={{ position: 'fixed', top: 44, left: 0, right: 0, zIndex: 100, background: navScrolled ? 'rgba(0,0,0,0.96)' : 'rgba(0,0,0,0.82)', backdropFilter: 'blur(14px)', borderBottom: navScrolled ? '1px solid #0f1e35' : '1px solid transparent', height: 60, display: 'flex', alignItems: 'center', padding: '0 4%', transition: 'all 0.3s ease', animation: 'navIn 0.5s ease' }}>
  <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <img src="/logo.png" style={{ height: 28, width: 28, borderRadius: 6 }} alt="SysNerve" />
<span style={{ fontSize: 18, fontWeight: 900, background: 'linear-gradient(135deg,#4a9eff,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Syne',sans-serif", letterSpacing: '-0.5px' }}>SysNerve</span>
    </div>
    <div className="nav-links-d" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
      <a href="#features" className="nav-link">Features</a>
      <a href="#how-it-works" className="nav-link">How It Works</a>
      <a href="#multilingual" className="nav-link">Multilingual</a>
      <a href="#pricing" className="nav-link">Pricing</a>
      {isSignedIn && (
        <button onClick={() => setShowHistory(!showHistory)} style={{ background: showHistory ? '#4a9eff18' : 'transparent', border: `1px solid ${showHistory ? '#4a9eff44' : '#1e2d45'}`, borderRadius: 8, color: showHistory ? '#4a9eff' : '#64748b', fontSize: 12, padding: '6px 12px', cursor: 'pointer', transition: 'all 0.2s' }}>
          📋 History ({history.length})
        </button>
      )}
      {isSignedIn ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>{user?.firstName || user?.emailAddresses?.[0]?.emailAddress}</span>
          <UserButton afterSignOutUrl="/" />
        </div>
      ) : (
        <SignInButton mode="modal">
          <button className="btn" style={{ background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Sign In →</button>
        </SignInButton>
      )}
    </div>
    <div className="mobile-signin" style={{ display: 'none' }}>
      {isSignedIn ? (
        <UserButton afterSignOutUrl="/" />
      ) : (
        <SignInButton mode="modal">
          <button style={{ background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, padding: '7px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Sign In →</button>
        </SignInButton>
      )}
    </div>
  </div>
</nav>

      {/* ── HISTORY PANEL ── */}
      {showHistory && isSignedIn && (
        <div style={{ position: 'fixed', top: 104, right: 0, width: 380, height: 'calc(100vh - 104px)', background: '#05080f', borderLeft: '1px solid #0f1e35', zIndex: 99, overflowY: 'auto', padding: 20, animation: 'fadeUp 0.3s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>📋 Bug History</h3>
            <button onClick={() => setShowHistory(false)} style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div style={{ background: '#000', borderRadius: 10, padding: '12px 16px', border: '1px solid #4a9eff22', marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 4, letterSpacing: '0.1em' }}>THIS SESSION</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#4a9eff', fontFamily: "'Syne',sans-serif" }}>{bugsAnalyzed}</div>
            <div style={{ fontSize: 11, color: '#334155' }}>bugs analyzed</div>
          </div>
          {history.length === 0 ? <div style={{ textAlign: 'center', color: '#475569', fontSize: 13, marginTop: 40 }}>No history yet. Analyze your first bug!</div>
            : history.map((item, i) => <HistoryItem key={i} item={item} onSelect={loadFromHistory} />)}
        </div>
      )}

      {/* ── HERO ── */}
      <section style={{ position: 'relative', zIndex: 1, paddingTop: 160, paddingBottom: 80, padding: '160px 5% 80px' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(#1e3a5f05 1px,transparent 1px),linear-gradient(90deg,#1e3a5f05 1px,transparent 1px)', backgroundSize: '56px 56px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '20%', left: '3%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(74,158,255,0.05) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <div style={{ animation: 'fadeUp 0.8s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#050810', border: '1px solid #1e3a5f', borderRadius: 24, padding: '6px 16px', marginBottom: 24 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1.5s infinite', boxShadow: '0 0 8px #4ade80' }} />
                <span style={{ fontSize: 13, color: '#94a3b8' }}><span style={{ color: '#4ade80', fontWeight: 700 }}>{(bugsAnalyzed + 1247).toLocaleString()}</span> bugs analyzed</span>
              </div>
              <h1 style={{ fontSize: 58, fontWeight: 900, lineHeight: 1.05, margin: '0 0 20px', letterSpacing: '-2px', color: '#fff' }}>
                AI that tells your team<br />
                <span style={{ background: 'linear-gradient(135deg,#4a9eff,#7c3aed,#4ade80)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 3s linear infinite' }}>
                  what's actually broken.
                </span>
              </h1>
              <p style={{ fontSize: 17, color: '#475569', margin: '0 0 12px', minHeight: 28 }}>
                <TypingText texts={['Senior Developer Level Debugging in 30s','AI-Powered Bug Intelligence Platform','Submit bugs in Any Language, Any Region','Attach Screenshots & Log Files']} />
              </p>
              <p style={{ fontSize: 15, color: '#475569', maxWidth: 540, margin: '0 0 36px', lineHeight: 1.85 }}>
                SysNerve analyzes bug reports, stack traces, screenshots, and log files. Get a full diagnosis — verdict, root cause, action plan — in seconds.
              </p>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 40 }}>
                <GlowButton className="btn analyze-glow" onClick={() => analyzerRef.current?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 700, padding: '15px 32px', cursor: 'pointer' }}>
                  Try SysNerve Free →
                </GlowButton>
                <button className="btn" onClick={() => analyzerRef.current?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ background: 'transparent', border: '1px solid #1e3a5f', borderRadius: 12, color: '#64748b', fontSize: 15, fontWeight: 600, padding: '15px 26px', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#4a9eff'; e.currentTarget.style.color = '#4a9eff' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#64748b' }}>
                  View Demo
                </button>
              </div>
              <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
                {[{num:1247,suffix:'+',label:'Bugs Analyzed'},{num:98,suffix:'%',label:'Accuracy Rate'},{num:5,suffix:'x',label:'Faster Debugging'}].map(({num,suffix,label}) => (
                  <div key={label} style={{ textAlign: 'center', padding: '14px 10px', background: '#050810', border: '1px solid #1e3a5f', borderRadius: 12 }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: '#4a9eff', fontFamily: "'Syne',sans-serif", lineHeight: 1.2 }}>
                      <AnimatedCounter target={num} suffix={suffix} />
                    </div>
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ animation: 'fadeUp 0.9s ease 0.2s both' }}><HeroDemoAnimation /></div>
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section style={{ padding: '24px 0 48px', position: 'relative', zIndex: 1, borderTop: '1px solid #0a1525', overflow: 'hidden' }}>
        <p style={{ textAlign: 'center', color: '#334155', fontSize: 11, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 20 }}>Trusted by developers using</p>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 48, animation: 'marquee 18s linear infinite', width: 'max-content' }}>
            {['Vercel','Next.js','Supabase','PostgreSQL','MongoDB','Docker','AWS','Cloudflare','Vercel','Next.js','Supabase','PostgreSQL','MongoDB','Docker','AWS','Cloudflare'].map((l, i) => (
              <span key={i} style={{ color: '#334155', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── MULTILINGUAL ── */}
      <section id="multilingual" className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1, background: '#020508' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ScrollReveal>
            <div className="multilingual-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.2)', borderRadius: 30, padding: '10px 20px', marginBottom: 24 }}>
                  <span style={{ fontSize: 22 }}>🌐</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#4a9eff', fontFamily: "'Syne',sans-serif" }}>Any Language, Any Region</span>
                </div>
                <h2 style={{ fontSize: 36, fontWeight: 900, margin: '0 0 18px', lineHeight: 1.15 }}>
                  Submit bug reports in<br />
                  <span style={{ color: '#4a9eff' }}>any language</span>
                </h2>
                <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.9, margin: '0 0 28px' }}>
                  SysNerve understands bug reports in any language. Write in your native language — our AI will analyze and respond intelligently.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {[
                    { lang: 'English', flag: '🇺🇸', ex: 'Login is broken' },
                    { lang: 'اردو', flag: '🇵🇰', ex: 'لاگ ان کام نہیں کر رہا' },
                    { lang: 'हिंदी', flag: '🇮🇳', ex: 'लॉगिन काम नहीं कर रहा' },
                    { lang: 'العربية', flag: '🇸🇦', ex: 'تسجيل الدخول لا يعمل' },
                    { lang: 'Français', flag: '🇫🇷', ex: 'La connexion ne fonctionne pas' },
                    { lang: '中文', flag: '🇨🇳', ex: '登录无法使用' },
                    { lang: 'Español', flag: '🇪🇸', ex: 'El login no funciona' },
                    { lang: 'Deutsch', flag: '🇩🇪', ex: 'Login funktioniert nicht' },
                  ].map(({ lang, flag, ex }) => (
                    <div key={lang} style={{ background: '#050810', border: '1px solid #1e3a5f', borderRadius: 10, padding: '10px 14px', transition: 'all 0.25s', cursor: 'default' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#4a9eff44'; e.currentTarget.style.background = 'rgba(74,158,255,0.05)' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.background = '#050810' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>{flag}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#4a9eff' }}>{lang}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic' }}>{ex}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: '#050810', border: '1px solid #1e3a5f', borderRadius: 18, padding: 28 }}>
                <div style={{ fontSize: 12, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 16 }}>LIVE MULTILINGUAL EXAMPLE</div>
                {[
                  { flag: '🇵🇰', input: 'Login kar raha tha, 500 error aa gaya deployment k baad', lang: 'Urdu' },
                  { flag: '🇸🇦', input: 'خطأ 500 عند تسجيل الدخول بعد النشر', lang: 'Arabic' },
                  { flag: '🇮🇳', input: 'डेप्लॉयमेंट के बाद लॉगिन में 500 एरर आ रहा है', lang: 'Hindi' },
                ].map(({ flag, input, lang }) => (
                  <div key={lang} style={{ marginBottom: 14, background: '#000', border: '1px solid #0f1e35', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                      <span style={{ fontSize: 14 }}>{flag}</span>
                      <span style={{ fontSize: 10, color: '#475569', fontWeight: 600, letterSpacing: '0.06em' }}>USER INPUT · {lang}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6, marginBottom: 8 }}>{input}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 800, flexShrink: 0 }}>AI</div>
                      <div style={{ fontSize: 11, color: '#4ade80', fontWeight: 600 }}>✓ Analyzed — REAL BUG · JWT token expiry issue detected</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <h2 style={{ fontSize: 38, fontWeight: 900, margin: '0 0 14px' }}>Everything Your Team Needs</h2>
              <p style={{ color: '#475569', fontSize: 15, margin: 0, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>SysNerve handles the investigation — your developers handle the fix.</p>
            </div>
          </ScrollReveal>
          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
            {[
              {icon:'🐛',title:'Real Bug Detection',desc:'Pinpoints exact file, function, and line number from stack traces.'},
              {icon:'❌',title:'NOT A BUG Detection',desc:'Instantly identifies user errors — no code change needed, time saved.'},
              {icon:'🖥️',title:'Infrastructure Diagnosis',desc:'Detects network, server, and third-party API issues.'},
              {icon:'📸',title:'Screenshot Analysis',desc:'Attach error screenshots — AI reads and diagnoses from the image.'},
              {icon:'📄',title:'Log File Support',desc:'Upload .log or .txt files — AI analyzes the full content.'},
              {icon:'⏱️',title:'Time Saved Tracking',desc:'Every analysis shows exactly how many hours your team saved.'},
            ].map(({icon,title,desc},i) => (
              <ScrollReveal key={title} delay={i * 75}>
                <div className="fcard" style={{ ...card, height: '100%' }}>
                  <div style={{ fontSize: 30, marginBottom: 14 }}>{icon}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', fontFamily: "'Syne',sans-serif" }}>{title}</h3>
                  <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.7 }}>{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1, background: '#020508' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <div style={{ fontSize: 11, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>How It Works</div>
              <h2 style={{ fontSize: 38, fontWeight: 900, margin: '0 0 14px' }}>3 Simple Steps to Clarity</h2>
              <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>From bug report to fix plan in under 30 seconds.</p>
            </div>
          </ScrollReveal>
          <HowItWorksDashboard />
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <h2 style={{ fontSize: 38, fontWeight: 900, margin: '0 0 12px' }}>Simple, Transparent Plans</h2>
              <p style={{ color: '#475569', fontSize: 14, margin: 0 }}>Start free. Upgrade when you're ready.</p>
            </div>
          </ScrollReveal>
          <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 22, alignItems: 'start' }}>
            {packages.map((pkg, i) => (
              <ScrollReveal key={pkg.name} delay={i * 100}>
                <div style={{ ...card, position: 'relative', border: `1px solid ${pkg.highlight ? '#4a9eff44' : '#0f1e35'}`, boxShadow: pkg.highlight ? '0 0 40px rgba(74,158,255,0.1)' : 'none', transition: 'all 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.transform='translateY(-6px)'} onMouseLeave={e => e.currentTarget.style.transform='translateY(0)'}>
                  {pkg.badge && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap', animation: 'glowPulse 2.5s ease-in-out infinite' }}>{pkg.badge}</div>}
                  <div style={{ fontSize: 11, color: pkg.highlight ? '#4a9eff' : '#475569', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 8 }}>{pkg.name.toUpperCase()}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                    <span style={{ fontSize: 38, fontWeight: 900, color: pkg.highlight ? '#4a9eff' : '#e2e8f0', fontFamily: "'Syne',sans-serif" }}>{pkg.price}</span>
                    <span style={{ fontSize: 13, color: '#334155' }}>{pkg.period}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: '0 0 24px' }}>{pkg.desc}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                    {pkg.features.map(f => (
                      <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span style={{ color: pkg.highlight ? '#4a9eff' : '#4ade80', fontSize: 13 }}>✓</span>
                        <span style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.4 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  {pkg.ctaAction === 'waitlist' ? (
                    <GlowButton className="btn" onClick={() => setShowWaitlist(true)}
                      style={{ width: '100%', padding: '12px', background: pkg.highlight ? 'linear-gradient(135deg,#1d4ed8,#4a9eff)' : 'transparent', border: `1px solid ${pkg.highlight ? '#4a9eff' : '#1e2d45'}`, borderRadius: 10, color: pkg.highlight ? '#fff' : '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      🚀 Join Waitlist
                    </GlowButton>
                  ) : (
                    <SignInButton mode="modal">
                      <GlowButton className="btn" style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid #1e2d45', borderRadius: 10, color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        Get Started
                      </GlowButton>
                    </SignInButton>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <Testimonials />

      {/* ── ABOUT ── */}
      <section id="about" className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1, background: '#020508' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div style={{ fontSize: 11, color: '#4a9eff', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>About SysNerve</div>
              <h2 style={{ fontSize: 34, fontWeight: 900, margin: '0 0 20px', lineHeight: 1.2 }}>Senior Developer Level Debugging — For Every Team Member</h2>
              <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.9, margin: '0 0 16px' }}>Every software team faces the same painful reality — bug reports flood in, developers spend hours investigating, and half the time it was not even a real bug.</p>
              <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.9, margin: 0 }}>SysNerve gives every team member the diagnostic power of a senior developer. In seconds, not hours.</p>
            </ScrollReveal>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[{label:'Average time saved per analysis',value:'3.2 hrs',color:'#4ade80'},{label:'NOT A BUG catch rate',value:'73%',color:'#4a9eff'},{label:'Teams using SysNerve',value:'Growing',color:'#7c3aed'}].map(({label,value,color},i) => (
                <ScrollReveal key={label} delay={i * 80}>
                  <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.25s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor=color+'44' }} onMouseLeave={e => { e.currentTarget.style.borderColor='#0f1e35' }}>
                    <span style={{ fontSize: 13, color: '#475569' }}>{label}</span>
                    <span style={{ fontSize: 22, fontWeight: 900, color, fontFamily: "'Syne',sans-serif" }}>{value}</span>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ANALYZER ── */}
      <section ref={analyzerRef} className="section-pad" style={{ padding: '80px 5%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 44 }}>
              <h2 style={{ fontSize: 36, fontWeight: 900, margin: '0 0 12px' }}>Try SysNerve Now</h2>
              <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>Senior Developer Level Debugging — in seconds.</p>
              {isSignedIn && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#050810', border: '1px solid #1e3a5f', borderRadius: 24, padding: '6px 16px', marginTop: 16 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1.5s infinite' }} />
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>You have analyzed <span style={{ color: '#4ade80', fontWeight: 700 }}>{bugsAnalyzed}</span> bugs</span>
                </div>
              )}
            </div>
          </ScrollReveal>

          <div className="analyzer-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span>📋</span><h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#94a3b8', fontFamily: "'Syne',sans-serif" }}>Bug Report</h3>
                </div>
                <textarea style={{ width: '100%', background: '#000', border: '1px solid #0f1e35', borderRadius: 10, color: '#e2e8f0', fontSize: 14, padding: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7, fontFamily: 'inherit', transition: 'all 0.3s', minHeight: 120 }}
                  placeholder="Paste the bug report here... any language supported — English, Urdu, Arabic, Hindi, French & more"
                  value={bugReport} onChange={e => setBugReport(e.target.value)} rows={5} />
              </div>

              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span>📟</span><h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#94a3b8', fontFamily: "'Syne',sans-serif" }}>Stack Trace / Error</h3>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#334155', background: '#000', padding: '2px 8px', borderRadius: 10, border: '1px solid #0f1e35' }}>optional</span>
                </div>
                <textarea style={{ width: '100%', background: '#000', border: '1px solid #0f1e35', borderRadius: 10, color: '#4ade80', fontSize: 13, padding: 14, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7, fontFamily: 'monospace', transition: 'all 0.3s' }}
                  placeholder="Paste error or stack trace here..." value={stackTrace} onChange={e => setStackTrace(e.target.value)} rows={4} />
              </div>

              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span>📎</span><h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#94a3b8', fontFamily: "'Syne',sans-serif" }}>Attachments</h3>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#334155', background: '#000', padding: '2px 8px', borderRadius: 10, border: '1px solid #0f1e35' }}>optional</span>
                </div>
                <div className="attach-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                    <button className="upload-btn" onClick={() => imageInputRef.current?.click()} style={{ width: '100%', padding: 14, background: '#000', border: '1px dashed #1e2d45', borderRadius: 10, color: '#64748b', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      📸 {attachedImageName || 'Attach Screenshot'}
                    </button>
                    {attachedImage && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, padding: '6px 10px', background: '#050810', borderRadius: 6, border: '1px solid #4ade8033' }}><span style={{ fontSize: 11, color: '#4ade80' }}>✓ {attachedImageName}</span><button onClick={() => { setAttachedImage(null); setAttachedImageName('') }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>✕</button></div>}
                  </div>
                  <div>
                    <input ref={fileInputRef} type="file" accept=".log,.txt,.csv,.json" onChange={handleFileUpload} style={{ display: 'none' }} />
                    <button className="upload-btn" onClick={() => fileInputRef.current?.click()} style={{ width: '100%', padding: 14, background: '#000', border: '1px dashed #1e2d45', borderRadius: 10, color: '#64748b', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      📄 {attachedFileName || 'Attach Log File'}
                    </button>
                    {attachedFile && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, padding: '6px 10px', background: '#050810', borderRadius: 6, border: '1px solid #4ade8033' }}><span style={{ fontSize: 11, color: '#4ade80' }}>✓ {attachedFileName}</span><button onClick={() => { setAttachedFile(null); setAttachedFileName('') }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>✕</button></div>}
                  </div>
                </div>
                <p style={{ fontSize: 11, color: '#334155', margin: '10px 0 0', textAlign: 'center' }}>Images: JPG, PNG (max 5MB) · Files: .log, .txt, .csv, .json (max 2MB)</p>
              </div>

              <GlowButton className="btn analyze-glow" onClick={handleAnalyze} disabled={loading}
                style={{ width: '100%', padding: 18, background: loading ? '#0a1525' : 'linear-gradient(135deg,#1d4ed8,#2563eb,#4a9eff)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 18, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                {loading ? <><div style={{ width: 20, height: 20, border: '2px solid #4a9eff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />{loadingSteps[loadingStep]}</> : 'Analyze with SysNerve →'}
              </GlowButton>

              <AIBrain active={loading} />
              <ResultDisplay result={result} analysisTime={analysisTime} />
            </div>

            {/* RIGHT SIDEBAR */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={card}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#e2e8f0', fontFamily: "'Syne',sans-serif" }}>Engineering Insights</h3>
                {[
                  {icon:'🎯',title:'AI Accuracy',desc:'Advanced AI for infrastructure, backend, and frontend systems.',color:'#4a9eff'},
                  {icon:'🌐',title:'Any Language',desc:'Submit bugs in English, Urdu, Arabic, Hindi, French & more.',color:'#4ade80'},
                  {icon:'⚡',title:'Instant Fix Suggestions',desc:'Actionable recommendations and debugging workflows instantly.',color:'#4a9eff'},
                  {icon:'🛡',title:'False Positive Detection',desc:'Saves time by identifying user errors and false alarms.',color:'#7c3aed'},
                ].map(({icon,title,desc,color}) => (
                  <div key={title} style={{ background: '#000', border: '1px solid #0f1e35', borderRadius: 12, padding: '14px 16px', marginBottom: 12, transition: 'all 0.25s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor=color+'44'} onMouseLeave={e => e.currentTarget.style.borderColor='#0f1e35'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: 16 }}>{icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color }}>{title}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>{desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ ...card, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#475569', letterSpacing: '0.1em', marginBottom: 8, textTransform: 'uppercase' }}>This Session</div>
                <div style={{ fontSize: 40, fontWeight: 900, color: '#4a9eff', fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>{bugsAnalyzed}</div>
                <div style={{ fontSize: 12, color: '#334155', marginBottom: 18, marginTop: 4 }}>bugs analyzed</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[{label:'Avg Time Saved',value:'3.2 hrs',color:'#4ade80'},{label:'NOT A BUG Rate',value:'73%',color:'#4a9eff'}].map(({label,value,color}) => (
                    <div key={label} style={{ background: '#000', border: `1px solid ${color}22`, borderRadius: 10, padding: '12px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Syne',sans-serif" }}>{value}</div>
                      <div style={{ fontSize: 10, color: '#334155', marginTop: 3 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding: '32px 5%', borderTop: '1px solid #0a1525', textAlign: 'center', position: 'relative', zIndex: 1 }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
    <img src="/logo.png" style={{ height: 24, width: 24, borderRadius: 4 }} alt="SysNerve" />
<span style={{ fontSize: 16, fontWeight: 900, background: 'linear-gradient(135deg,#4a9eff,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Syne',sans-serif" }}>SysNerve</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
    <Link to="/faq" style={{ fontSize: 12, color: '#475569', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#4a9eff'} onMouseLeave={e => e.target.style.color='#475569'}>FAQ</Link>

    <span style={{ color: '#1e293b', fontSize: 10 }}>·</span>
    <Link to="/privacy" style={{ fontSize: 12, color: '#475569', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#4a9eff'} onMouseLeave={e => e.target.style.color='#475569'}>Privacy Policy</Link>

    <span style={{ color: '#1e293b', fontSize: 10 }}>·</span>
<Link to="/terms" style={{ fontSize: 12, color: '#475569', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color='#4a9eff'} onMouseLeave={e => e.target.style.color='#475569'}>Terms of Service</Link>
  </div>
  <p style={{ color: '#1e293b', fontSize: 12, margin: 0 }}>© 2026 SysNerve — AI Bug Intelligence Platform | Powered by AI Engine</p>
</footer>
      <Analytics />
    </div>
  )
}
